import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTypewriter, Cursor } from 'react-simple-typewriter';
import Button from "../../components/common/Button";
import useAuth from "../../Hooks/useAuth";
import useApiCalls from "../../Hooks/useApiCalls";
import { NotificationManagerSuccess, NotificationManagerError } from "../../utils/Notification";
import "./Onboarding.css";

// Endpoint for updating user preferences
const UPDATE_USER_PREFERENCES = "/auth/preferences";

// Therapy style options
const therapyStyleOptions = [
  { value: 'supportive', label: 'Supportive', description: 'Empathetic and understanding' },
  { value: 'directive', label: 'Directive', description: 'Offers clear guidance' },
  { value: 'reflective', label: 'Reflective', description: 'Helps you explore your thoughts' }
];

// Therapy modality options
const modalityOptions = [
  { value: 'cbt', label: 'CBT', description: 'Cognitive Behavioral Therapy' },
  { value: 'psychodynamic', label: 'Psychodynamic', description: 'Explores unconscious patterns' },
  { value: 'humanistic', label: 'Humanistic', description: 'Focuses on personal growth' }
];

// Check-in options
const checkInOptions = [
  { value: 'periodic', label: 'Check in periodically', description: 'Regular follow-ups' },
  { value: 'appropriate', label: 'Check in when appropriate', description: 'Follow up only when it seems necessary' },
  { value: 'none', label: 'Don\'t check in', description: 'No check-ins unless requested' }
];

// Texts for each intro modal
const introTexts = [
  "Hi, I'm your AI Therapist",
  "What's your name?",
  "Great to meet you, [name]!\n\nBefore we customize this experience for you, I'd like to tell you a few things about how this works.",
  "I know that every person is unique, so your experience with me should be fully customized to be truly effective.\n\nAfter a few quick steps, we'll start customizing the experience for you.",
  "The more we talk, the better I'll understand your thoughts, feelings, and experiences. This helps me support you in the way that's right for you.",
  "Whether it's daily frustrations, overwhelming challenges or past trauma, I'm here to listen and help you make sense of it all.\n\nOur chats are a private, safe, and judgment-free space."
];

// Texts for each customize modal
const customizeTexts = [
  {
    question: "What's your experience level with therapy?",
    subtitle: "Help me understand your background",
    icon: "🧠",
    options: [
      {
        value: "beginner",
        title: "Just getting started",
        subtitle: "First time trying therapy"
      },
      {
        value: "some",
        title: "Some experience",
        subtitle: "Been to a few sessions before"
      },
      {
        value: "significant",
        title: "Veteran",
        subtitle: "Regular therapy participant"
      }
    ]
  },
  {
    question: "What therapy style resonates with you?",
    subtitle: "Choose the therapeutic approach that feels right for you",
    icon: "💡",
    options: [
      {
        value: "supportive",
        title: "Supportive",
        subtitle: "Empathetic and understanding approach"
      },
      {
        value: "directive",
        title: "Directive",
        subtitle: "Clear guidance and structure"
      },
      {
        value: "reflective",
        title: "Reflective",
        subtitle: "Helps you explore your thoughts"
      }
    ]
  },
  "Different therapeutic approaches have different benefits.\n\nSelect which one resonates with you most."
];

// Add the new SilentTyper component
const SilentTyper = ({ text, onDone, className = '', typeSpeed = 100 }) => {
  const [typed, helper] = useTypewriter({
    words: [text],
    loop: 1,
    typeSpeed,
  });

  useEffect(() => {
    if (helper.isDone) onDone?.();
  }, [helper.isDone, onDone]);

  return (
    <span className={`silent-typer ${className}`}>
      {typed}
      {!helper.isDone && <Cursor />}
    </span>
  );
};

const DEBUG_SKIP_INTRO = false; // Set to false to restore full onboarding

// Add a mapping for the processing modal text
const processingMessages = {
  beginner: {
    main: "Optimizing for first-time therapy experience...",
    secondary: "Loading supportive resources for new clients..."
  },
  some: {
    main: "Optimizing for returning therapy clients...",
    secondary: "Loading personalized strategies for your journey..."
  },
  significant: {
    main: "Optimizing for experienced therapy participants...",
    secondary: "Loading advanced therapeutic methodologies..."
  },
  default: {
    main: "Optimizing for advanced therapeutic concepts...",
    secondary: "Loading advanced therapeutic methodologies..."
  }
};

// Add a mapping for the processing modal text for styles
const styleProcessingMessages = {
  supportive: {
    main: "Configuring a supportive, empathetic approach...",
    secondary: "Loading understanding and compassionate guidance..."
  },
  directive: {
    main: "Configuring a directive, structured approach...",
    secondary: "Loading clear guidance and practical strategies..."
  },
  reflective: {
    main: "Configuring a reflective, exploratory approach...",
    secondary: "Loading tools for self-discovery and insight..."
  },
  default: {
    main: "Configuring your personalized support style...",
    secondary: "Loading your selected preferences..."
  }
};

// Add a mapping for the processing modal text for modalities
const modalityProcessingMessages = {
  cbt: {
    main: "Configuring a cognitive-behavioral approach...",
    secondary: "Loading CBT resources and strategies..."
  },
  psychodynamic: {
    main: "Configuring a psychodynamic approach...",
    secondary: "Loading tools for exploring unconscious patterns..."
  },
  humanistic: {
    main: "Configuring a humanistic approach...",
    secondary: "Loading resources for personal growth..."
  },
  default: {
    main: "Configuring your selected modality...",
    secondary: "Loading resources for your preferences..."
  }
};

// Add this helper function near the top with other functions
const isValidName = (name) => {
  return name.trim().length > 0;
};

function Onboarding() {
  const navigate = useNavigate();
  const api = useApiCalls();
  
  // State for tracking which section we're in (intro or customize)
  const [section, setSection] = useState(DEBUG_SKIP_INTRO ? 'customize' : 'intro');
  
  // Step counters for each section
  const [introStep, setIntroStep] = useState(1);
  const [customizeStep, setCustomizeStep] = useState(1);
  
  // Add independent state for each modal
  const [intro1Done, setIntro1Done] = useState(false);
  const [intro2Done, setIntro2Done] = useState(false);
  const [intro3Part, setIntro3Part] = useState(1); // 1 or 2
  const [intro3Done, setIntro3Done] = useState(false);
  const [intro4Done, setIntro4Done] = useState(false);
  const [intro5Done, setIntro5Done] = useState(false);
  const [intro6Done, setIntro6Done] = useState(false);
  
  // Total steps in intro section
  const totalIntroSteps = 6;
  // Total steps in customize section
  const totalCustomizeSteps = 4;
  
  // Form data
  const [name, setName] = useState("");
  const [experience, setExperience] = useState("beginner"); // Step 1 default
  const [therapyStyle, setTherapyStyle] = useState("supportive"); // Step 2 default
  const [modality, setModality] = useState("cbt"); // Step 3 default
  const [checkInPreference, setCheckInPreference] = useState("periodic"); // Step 4 default

  const typeSpeed = 40;

  // Add state for processing modal
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingDone, setProcessingDone] = useState(false);
  const [progress, setProgress] = useState(0);

  // Add state for multi-select styles
  const [selectedStyles, setSelectedStyles] = useState([]);

  // Add a processingStep state
  const [processingStep, setProcessingStep] = useState(null); // null, 'experience', or 'styles'

  // Add state for check-in consent
  const [checkInConsent, setCheckInConsent] = useState(true);

  // Add loading state for submission
  const [submitting, setSubmitting] = useState(false);

  // Reset only relevant state on step change
  useEffect(() => {
    if (introStep === 1) setIntro1Done(false);
    if (introStep === 2) setIntro2Done(false);
    if (introStep === 3) { setIntro3Part(1); setIntro3Done(false); }
    if (introStep === 4) setIntro4Done(false);
    if (introStep === 5) setIntro5Done(false);
    if (introStep === 6) setIntro6Done(false);
  }, [introStep]);

  // Modal 3: backup timeout for part 2 only


  // Handle next button for intro section
  const handleIntroNext = () => {
    if (introStep < totalIntroSteps) {
      setIntroStep(introStep + 1);
    } else {
      // Move to customize section after intro is complete
      setSection('customize');
      setCustomizeStep(1);
    }
  };

  // Handle next button for customize section
  const handleCustomizeNext = () => {
    if (customizeStep < totalCustomizeSteps) {
      setCustomizeStep(customizeStep + 1);
      // Scroll to top on mobile
      if (window.innerWidth <= 768) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      handleSubmit();
    }
  };

  // Handle back button for customize section
  const handleCustomizeBack = () => {
    if (customizeStep > 1) {
      setCustomizeStep(customizeStep - 1);
    } else {
      // Go back to intro section if at first customize step
      setSection('intro');
      setIntroStep(totalIntroSteps);
    }
  };


  const handleSubmit = async () => {
    try {
      // Send the preferences to the backend
      const res = await api(UPDATE_USER_PREFERENCES, 'post', {
        name,
        experience,
        therapyStyle,
        modality
      });
      
      if (res.status === 200) {
        NotificationManagerSuccess(res?.data?.message || "Your preferences have been saved!");
        navigate("/", { replace: true });
      } else {
        NotificationManagerError(res?.data?.error || "Failed to save your preferences.");
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      NotificationManagerError("An error occurred while saving your preferences.");
    }
  };

  // Update handleShowProcessing to accept a step argument
  const handleShowProcessing = (step) => {
    setProcessingStep(step);
    setShowProcessingModal(true);
    setProcessingDone(false);
    setProgress(0);

    let prog = 0;
    const interval = setInterval(() => {
      prog += 2;
      setProgress(Math.min(prog, 100));
      if (prog >= 100) {
        clearInterval(interval);
        setProcessingDone(true);
        setTimeout(() => {
          setShowProcessingModal(false);
          setProcessingStep(null);
          handleCustomizeNext();
        }, 900);
      }
    }, 60);
  };

  // Render the intro section
  const renderIntroSection = () => {
    return (
      <>
        {/* Step 1: Welcome with typing animation */}
        {introStep === 1 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <h1 style={{ maxWidth: '80%', margin: '0 auto', fontSize: '2rem', textAlign: 'center' }}>
                  <SilentTyper text={introTexts[0]} onDone={() => setIntro1Done(true)} typeSpeed={typeSpeed} />
                </h1>
              </div>
              <div
                className="intro-button-container"
                style={{
                  opacity: intro1Done ? 1 : 0,
                  pointerEvents: intro1Done ? 'auto' : 'none',
                  transition: 'opacity 0.5s',
                  minHeight: 56 // Reserve space for the button
                }}
              >
                <Button
                  title="Continue"
                  className="btn-primary btn-fit-content"
                  onClick={handleIntroNext}
                />
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
        {/* Step 2: Name input - simplified */}
        {introStep === 2 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <h1 style={{ maxWidth: '80%', margin: '0 auto', fontSize: '2rem' }}>
                  <SilentTyper text={introTexts[1]} onDone={() => setIntro2Done(true)} typeSpeed={typeSpeed} />
                </h1>
              </div>
              <div
                className="name-input-container"
                style={{
                  opacity: intro2Done ? 1 : 0,
                  pointerEvents: intro2Done ? 'auto' : 'none',
                  transition: 'opacity 0.5s',
                  minHeight: 120
                }}
              >
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value.trimStart())}
                    onBlur={(e) => setName(e.target.value.trim())}
                    placeholder="Your name"
                    autoFocus={intro2Done}
                  />
                </div>
                <div className="d-flex justify-content-center">
                  <Button 
                    title="Continue" 
                    className="btn-primary" 
                    onClick={handleIntroNext}
                    disabled={!isValidName(name)}
                  />
                </div>
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
        {/* Step 3: Greeting with name */}
        {introStep === 3 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <h1 style={{ maxWidth: '100%', width: '100%', margin: '0 auto', fontSize: '2rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.4', marginBottom: '15px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  <SilentTyper text={`Great to meet you, ${name || "there"}!`} cursor={false} onDone={() => setIntro3Part(2)} typeSpeed={typeSpeed} />
                </h1>
                {intro3Part === 2 && (
                  <div
                    style={{
                      fontSize: '1.2rem',
                      lineHeight: '1.5',
                      fontWeight: 400,
                      minHeight: 28, // Reserve space for the second line
                      textAlign: 'center',
                      opacity: 1,
                      transition: 'opacity 0.5s',
                      pointerEvents: 'auto'
                    }}
                  >
                    <SilentTyper text={`Before we customize this experience for you, I'd like to tell you a few things about how this works.`} onDone={() => setIntro3Done(true)} typeSpeed={typeSpeed} />
                  </div>
                )}
                <div
                  className="intro-button-container"
                  style={{
                    opacity: intro3Done ? 1 : 0,
                    pointerEvents: intro3Done ? 'auto' : 'none',
                    transition: 'opacity 0.5s',
                    minHeight: 56 // Reserve space for the button
                  }}
                >
                  <Button
                    title="How this works"
                    className="btn-primary btn-fit-content"
                    onClick={handleIntroNext}
                  />
                </div>
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
        {/* Step 4: Customized experience */}
        {introStep === 4 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <div style={{ fontSize: '1.5rem', lineHeight: '1.5', fontWeight: 400, textAlign: 'center', minHeight: 28 }}>
                  <SilentTyper text={introTexts[3]} onDone={() => setIntro4Done(true)} typeSpeed={typeSpeed} />
                </div>
              </div>
              <div
                className="intro-button-container"
                style={{
                  opacity: intro4Done ? 1 : 0,
                  pointerEvents: intro4Done ? 'auto' : 'none',
                  transition: 'opacity 0.5s',
                  minHeight: 56
                }}
              >
                <Button
                  title="Continue"
                  className="btn-primary btn-fit-content"
                  onClick={handleIntroNext}
                />
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
        {/* Step 5: Understanding */}
        {introStep === 5 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <div style={{ fontSize: '1.5rem', lineHeight: '1.5', fontWeight: 400, textAlign: 'center', minHeight: 28 }}>
                  <SilentTyper text={introTexts[4]} onDone={() => setIntro5Done(true)} typeSpeed={typeSpeed} />
                </div>
              </div>
              <div
                className="intro-button-container"
                style={{
                  opacity: intro5Done ? 1 : 0,
                  pointerEvents: intro5Done ? 'auto' : 'none',
                  transition: 'opacity 0.5s',
                  minHeight: 56
                }}
              >
                <Button
                  title="Continue"
                  className="btn-primary btn-fit-content"
                  onClick={handleIntroNext}
                />
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
        {/* Step 6: Safe space */}
        {introStep === 6 && (
          <div className="onboarding-step intro-step">
            <div className="intro-content-wrapper">
              <div className="intro-text-container">
                <div style={{ fontSize: '1.5rem', lineHeight: '1.5', fontWeight: 400, textAlign: 'center', minHeight: 28 }}>
                  <SilentTyper text={introTexts[5]} onDone={() => setIntro6Done(true)} typeSpeed={typeSpeed} />
                </div>
              </div>
              <div
                className="intro-button-container"
                style={{
                  opacity: intro6Done ? 1 : 0,
                  pointerEvents: intro6Done ? 'auto' : 'none',
                  transition: 'opacity 0.5s',
                  minHeight: 56 // Reserve space for the button
                }}
              >
                <Button
                  title="Let's begin"
                  className="btn-primary btn-fit-content"
                  onClick={handleIntroNext}
                />
              </div>
            </div>
            <div className="step-indicators">
              {Array.from({ length: totalIntroSteps }).map((_, index) => (
                <div
                  key={`intro-step-${index + 1}`}
                  className={`step-indicator ${introStep >= index + 1 ? 'active' : ''}`}
                ></div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  // Render the customize section
  const renderCustomizeSection = () => {
    return (
      <div className="customize-section">
        <div className="customize-header-row">
          <div className="customize-header-left">
            <h3 className="customize-title">Customize Your Experience</h3>
            <div className="step-indicator">
              Step {customizeStep} of {totalCustomizeSteps}
            </div>
          </div>
          <div className="customize-header-right">
            <div className="onboard-progress-bar">
              <div
                className="onboard-progress-fill"
                style={{
                  width: `${(customizeStep / totalCustomizeSteps) * 100}%`,
                  background: 'linear-gradient(90deg, #a259f7 0%, #4a9fd8 100%)',
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="customize-content">
          {/* Step 1: Therapy Experience */}
          {customizeStep === 1 && (
            <div className="customize-step">
              <div className="customize-question-row">
                <span className="customize-icon">{customizeTexts[0].icon}</span>
                <div>
                  <div className="customize-question">{customizeTexts[0].question}</div>
                  <div className="customize-question-sub">{customizeTexts[0].subtitle}</div>
                </div>
              </div>
              <div className="customize-options-row">
                {customizeTexts[0].options.map(option => (
                  <div
                    key={option.value}
                    className={`customize-option-box${experience === option.value ? ' selected' : ''}`}
                    onClick={() => setExperience(option.value)}
                  >
                    <div className="customize-option-title">{option.title}</div>
                    <div className="customize-option-sub">{option.subtitle}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Therapy Style */}
          {customizeStep === 2 && (
            <div className="customize-step">
              <div className="customize-question-row">
                <span className="customize-icon">{customizeTexts[1].icon}</span>
                <div>
                  <div className="customize-question">{customizeTexts[1].question}</div>
                  <div className="customize-question-sub">{customizeTexts[1].subtitle}</div>
                </div>
              </div>
              <div className="customize-options-row customize-options-grid">
                {customizeTexts[1].options.map(option => (
                  <div
                    key={option.value}
                    className={`customize-option-box${therapyStyle === option.value ? ' selected' : ''}`}
                    onClick={() => setTherapyStyle(option.value)}
                  >
                    <div className="customize-option-title">{option.title}</div>
                    <div className="customize-option-sub">{option.subtitle}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Therapy Modality */}
          {customizeStep === 3 && (
            <div className="customize-step">
              <div className="customize-question-row">
                <span className="customize-icon">🧰</span>
                <div>
                  <div className="customize-question">What therapy modality suits you best?</div>
                  <div className="customize-question-sub">Choose one</div>
                </div>
              </div>
              <div className="customize-options-row customize-options-grid">
                {modalityOptions.map(option => (
                  <div
                    key={option.value}
                    className={`customize-option-box${modality === option.value ? ' selected' : ''}`}
                    onClick={() => setModality(option.value)}
                  >
                    <div className="customize-option-title">{option.label}</div>
                    <div className="customize-option-sub">{option.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Final Step: Completion and Launch */}
          {customizeStep === totalCustomizeSteps && (
            <div className="customize-step final-step">
              <div className="final-step-card">
                <div className="final-title">Therapy Customization Complete!</div>
                <p className="final-desc">
                  You're all set! Your preferences have been saved and your experience is ready to begin.<br /><br />
                    Don't worry, you can always change these settings later in your profile.
                </p>
                <div className="final-actions">
                  <Button
                    title="Start Texting"
                    className="btn-primary me-2"
                    onClick={() => handleLaunchChat('text')}
                    disabled={submitting}
                  />
                  <Button
                    title="Voice Chat"
                    className="btn-secondary me-2"
                    onClick={() => handleLaunchChat('voice')}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="customize-navigation">
          {customizeStep !== totalCustomizeSteps && (
            <>
              <Button
                title="Back"
                className="btn-secondary me-2"
                onClick={handleCustomizeBack}
                disabled={customizeStep === 1}
              />
              <Button
                title={customizeStep === totalCustomizeSteps ? "Get Started" : "Next"}
                className="btn-primary ms-2"
                onClick={() => handleShowProcessing(
                  customizeStep === 1 ? 'experience' :
                  (customizeStep === 2 ? 'styles' :
                  (customizeStep === 3 ? 'modality' : null))
                )}
                disabled={false}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  // Update handleLaunchChat to submit preferences before navigating
  const handleLaunchChat = async (mode) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Submit preferences before navigating
      const res = await api(UPDATE_USER_PREFERENCES, 'post', {
        name,
        experience,
        therapyStyle,
        modality
      });
      if (res.status === 200) {
        NotificationManagerSuccess(res?.data?.message || "Your preferences have been saved!");
        // Navigate to different routes based on mode
        if (mode === 'voice') {
          // For voice chat after onboarding, assume it's a new session, so no ID yet.
          // Backend will create one and that will become primaryRealtimeChatId.
          localStorage.removeItem('primaryRealtimeChatId'); // Clear any old one
          navigate('/realtime-chat', { state: { mode, checkInConsent } });
        } else {
          // After onboarding, user is starting their first chat, so navigate to /chat for intro.
          // Clear any primaryChatId that might exist if they are re-onboarding or from an old state.
          localStorage.removeItem('primaryChatId');
          navigate('/chat', { state: { mode, checkInConsent } });
        }
      } else {
        NotificationManagerError(res?.data?.error || "Failed to save your preferences.");
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      NotificationManagerError("An error occurred while saving your preferences.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-bg">
      <div className="modal-page-flex">
        <div className="modal-page-flex-main">
          <div className="modal-page">
            {section === 'intro' ? renderIntroSection() : renderCustomizeSection()}
          </div>
        </div>
      </div>
      {showProcessingModal && (
        <div className="processing-modal-overlay">
          <div className="processing-modal">
            <div className="processing-icon">
              {!processingDone ? (
                <span className="spinner"></span>
              ) : (
                <span className="checkmark">&#10003;</span>
              )}
            </div>
            {(() => {
              if (processingStep === 'experience') {
                const selectedProcessing = processingMessages[experience] || processingMessages.default;
                return <>
                  <div className={`processing-status ${processingDone ? 'done' : ''}`}>
                    {processingDone
                      ? selectedProcessing.main.replace("Optimizing", "Optimized")
                      : selectedProcessing.main}
                  </div>
                  <div className="processing-status-secondary">
                    {selectedProcessing.secondary}
                  </div>
                </>;
              } else if (processingStep === 'styles') {
                if (selectedStyles.length === 1) {
                  // Single selection: show both main and secondary
                  const styleKey = selectedStyles[0];
                  const msg = styleProcessingMessages[styleKey] || styleProcessingMessages.default;
                  return (
                    <>
                      <div className={`processing-status ${processingDone ? 'done' : ''}`}>
                        {processingDone
                          ? msg.main.replace("Configuring", "Configured")
                          : msg.main}
                      </div>
                      <div className="processing-status-secondary">
                        {msg.secondary}
                      </div>
                    </>
                  );
                } else {
                  // Multi-select: show only secondary texts
                  const selectedMessages = selectedStyles.length
                    ? selectedStyles.map(styleKey => styleProcessingMessages[styleKey] || styleProcessingMessages.default)
                    : [styleProcessingMessages.default];
                  const secondaryText = selectedMessages.map(msg => msg.secondary).join('\n');
                  return (
                    <div className="processing-status-secondary">
                      {secondaryText.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                    </div>
                  );
                }
              } else if (processingStep === 'modality') {
                const msg = modalityProcessingMessages[modality] || modalityProcessingMessages.default;
                return (
                  <>
                    <div className={`processing-status ${processingDone ? 'done' : ''}`}>
                      {processingDone
                        ? msg.main.replace("Configuring", "Configured")
                        : msg.main}
                    </div>
                    <div className="processing-status-secondary">
                      {msg.secondary}
                    </div>
                  </>
                );
              } else {
                // fallback
                return null;
              }
            })()}
            <div className="processing-progress-bar">
              <div
                className="processing-progress-fill"
                style={{
                  width: `${progress}%`,
                  background: processingDone
                    ? "linear-gradient(90deg, #00e676 0%, #00c853 100%)"
                    : "linear-gradient(90deg, #a259f7 0%, #4a9fd8 100%)"
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Onboarding;